import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { i18n } from '@/utils/i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@vaiu/ui/card";
import { Button } from "@vaiu/ui/button";
import { Label } from "@vaiu/ui/label";
import { Separator } from "@vaiu/ui/separator";
import { Alert, AlertDescription } from "@vaiu/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@vaiu/ui/select';
import { 
  ArrowLeft,
  Wallet,
  CreditCard,
  Building2,
  ArrowUpRight,
  AlertTriangle,
} from 'lucide-react';
import { toast } from "sonner";
import {
  CurrencyDisplay,
  TransferOutAmountCard,
  TransferOutMethodOption,
} from '@vaiu/ui';
import { ProfileTransferOutPayoutsCard } from '@/components/profile/ProfileTransferOutPayoutsCard';
import {
  mergeStripeConnectFromMerchantStatus,
  mergeStripeConnectIdIntoProfile,
} from '@/utils/profileStripeConnect';
import { platformApi, PaymentMethod } from '@/services/platformApi';
import { currencyService } from '@/services/currencyService';
import {
  isBankTransferOutChannelEnabled,
  isPayPalPayoutDestinationReady,
  isPaypalTransferOutChannelEnabled,
  isStripeConnectTransferOutAvailable,
  mergeTransferOutPreferencePayloadWithProfile,
} from '@vaiu/utils';

type TransferMethodType = 'paypal' | 'stripe' | 'plaid' | 'stripe-connect';

type TransferMethodRow = PaymentMethod & { methodType: TransferMethodType };

interface TransferOutPageProps {}

const TransferOutPage: React.FC<TransferOutPageProps> = () => {
  const navigate = useNavigate();
  const { currency: currencyParam } = useParams<{ currency?: string }>();
  
  const [balance, setBalance] = useState<number>(0);
  const [transferAmount, setTransferAmount] = useState<string>('');
  const [selectedCurrency, setSelectedCurrency] = useState<string>(currencyParam?.toUpperCase() ?? '');
  const [selectedMethod, setSelectedMethod] = useState<TransferMethodRow | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [mergedBankMethods, setMergedBankMethods] = useState<PaymentMethod[]>([]);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [availableBalances, setAvailableBalances] = useState<Record<string, number>>({});
  const [acceptableCurrencies, setAcceptableCurrencies] = useState<{
    paypal: string[];
    stripe: string[];
    bank: string[];
    all: string[];
  }>({ paypal: [], stripe: [], bank: [], all: [] });
  const [loading, setLoading] = useState(true);
  const [transferLoading, setTransferLoading] = useState(false);
  const [estimatedFees, setEstimatedFees] = useState<{
    amount: number;
    percentage: number;
    fixed: number;
  }>({ amount: 0, percentage: 0, fixed: 0 });
  const [transferOutPreferences, setTransferOutPreferences] = useState<any>(null);
  const [payoutsProfileSnapshot, setPayoutsProfileSnapshot] = useState<{
    roles: string[];
    profile: {
      paypalEmail?: string;
      paypalEmailVerified?: boolean;
      transferOutPreferences?: Record<string, { enabled?: boolean }>;
      stripeConnectId?: string;
    };
  } | null>(null);

  useEffect(() => {
    loadTransferData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTransferData = async () => {
    try {
      setLoading(true);
      
      const balanceData = await platformApi.getBalances();
      setAvailableBalances(balanceData || {});
      
      const methods = await platformApi.getPaymentMethods();
      setPaymentMethods(methods);

      let stripeBanks: PaymentMethod[] = [];
      try {
        stripeBanks = await platformApi.getStripeBankAccounts();
      } catch (e) {
        console.warn('TransferOut: could not load Stripe bank accounts', e);
      }

      const plaidFromApi = methods.filter((m) => m.type === 'plaid');
      const byId = new Map<string, PaymentMethod>();
      for (const m of [...plaidFromApi, ...stripeBanks]) {
        if (m.id) byId.set(m.id, m);
      }
      setMergedBankMethods([...byId.values()]);
      
      const currencies = await platformApi.getAcceptableCurrencies();
      setAcceptableCurrencies(currencies);
      
      const [prefs, profilePayload] = await Promise.all([
        platformApi.getPreferences('transfer-out'),
        platformApi.getProfile(),
      ]);
      setUserRoles(Array.isArray(profilePayload?.roles) ? profilePayload.roles : []);

      const payoutProf = profilePayload as Record<string, unknown> & {
        profile?: Record<string, unknown>;
        roles?: string[];
      };
      if (payoutProf.profile == null || typeof payoutProf.profile !== 'object') {
        payoutProf.profile = {};
      }
      mergeStripeConnectIdIntoProfile(payoutProf);
      await mergeStripeConnectFromMerchantStatus(payoutProf, () =>
        platformApi.merchant.getMerchantStripeConnectStatus()
      );

      setTransferOutPreferences(
        mergeTransferOutPreferencePayloadWithProfile(prefs, profilePayload)
      );

      const p = payoutProf.profile as {
        paypalEmail?: string;
        paypalEmailVerified?: boolean;
        transferOutPreferences?: Record<string, { enabled?: boolean }>;
        stripeConnectId?: string;
      };
      setPayoutsProfileSnapshot({
        roles: Array.isArray(payoutProf.roles) ? (payoutProf.roles as string[]) : [],
        profile: {
          paypalEmail: p.paypalEmail,
          paypalEmailVerified: p.paypalEmailVerified,
          transferOutPreferences: p.transferOutPreferences,
          stripeConnectId: p.stripeConnectId,
        },
      });
      
    } catch (error) {
      console.error('Failed to load transfer data:', error);
      toast.error(i18n.__('transferOut.toast.error'));
      setTransferOutPreferences({});
      setPayoutsProfileSnapshot(null);
    } finally {
      setLoading(false);
    }
  };

  const isMerchant = userRoles.includes('MERCHANT');
  const availableCurrencyOptions = useMemo(
    () => Object.entries(availableBalances).sort(([a], [b]) => a.localeCompare(b)),
    [availableBalances]
  );
  const cur = selectedCurrency || currencyParam?.toUpperCase() || '';

  useEffect(() => {
    if (availableCurrencyOptions.length === 0) return;

    const routeCurrency = currencyParam?.toUpperCase() || '';
    const availableCodes = availableCurrencyOptions.map(([code]) => code);

    if (routeCurrency && availableCodes.includes(routeCurrency)) {
      if (selectedCurrency !== routeCurrency) {
        setSelectedCurrency(routeCurrency);
      }
      return;
    }

    if (!selectedCurrency || !availableCodes.includes(selectedCurrency)) {
      setSelectedCurrency(availableCodes[0]);
    }
  }, [availableCurrencyOptions, currencyParam, selectedCurrency]);

  useEffect(() => {
    if (!selectedCurrency) {
      setBalance(0);
      return;
    }
    setBalance(availableBalances[selectedCurrency] ?? 0);
  }, [availableBalances, selectedCurrency]);

  const handleCurrencyChange = (nextCurrency: string) => {
    if (!nextCurrency || nextCurrency === selectedCurrency) return;
    setSelectedCurrency(nextCurrency);
    setTransferAmount('');
    setSelectedMethod(null);
    setBalance(availableBalances[nextCurrency] ?? 0);
    navigate(`/transfer-out/${encodeURIComponent(nextCurrency)}`);
  };

  const getAvailableTransferMethods = (): TransferMethodRow[] => {
    const methods: TransferMethodRow[] = [];
    if (!cur) return methods;
    
    if (
      acceptableCurrencies.paypal &&
      acceptableCurrencies.paypal.includes(cur) &&
      isPaypalTransferOutChannelEnabled(transferOutPreferences) &&
      isPayPalPayoutDestinationReady(transferOutPreferences)
    ) {
      const paypalEmail = transferOutPreferences?.paypalEmail || '';
      methods.push({
        id: 'paypal-payout',
        type: 'paypal',
        details: {
          email: paypalEmail
        },
        isDefault: false,
        createdAt: new Date().toISOString(),
        methodType: 'paypal' as const
      });
    }

    if (isBankTransferOutChannelEnabled(transferOutPreferences)) {
      const bankMethods = mergedBankMethods.filter(m => 
        m.type === 'plaid' && acceptableCurrencies.bank.includes(cur)
      );
      methods.push(...bankMethods.map(m => ({ ...m, methodType: 'plaid' as const })));
    }

    if (
      isMerchant &&
      acceptableCurrencies.stripe?.includes(cur) &&
      isStripeConnectTransferOutAvailable(transferOutPreferences)
    ) {
      methods.push({
        id: 'stripe-connect-payout',
        type: 'stripe',
        details: {
          last4: (transferOutPreferences?.stripeConnectId ?? '').slice(-4) || '****',
          brand: 'connect',
        },
        isDefault: false,
        createdAt: new Date().toISOString(),
        methodType: 'stripe-connect' as const,
      });
    }
    
    return methods;
  };

  useEffect(() => {
    if (transferAmount && selectedMethod) {
      const amount = parseFloat(transferAmount);
      if (!isNaN(amount) && amount > 0) {
        let percentage = 0;
        let fixed = 0;
        
        switch (selectedMethod.methodType) {
          case 'paypal':
            percentage = 0.025;
            fixed = 0.30;
            break;
          case 'stripe':
          case 'stripe-connect':
            percentage = 0.029;
            fixed = 0.30;
            break;
          case 'plaid':
            percentage = 0.01;
            fixed = 0.25;
            break;
        }
        
        const calculatedFees = (amount * percentage) + fixed;
        setEstimatedFees({
          amount: calculatedFees,
          percentage,
          fixed
        });
      }
    } else {
      setEstimatedFees({ amount: 0, percentage: 0, fixed: 0 });
    }
  }, [transferAmount, selectedMethod]);

  const handleTransferOut = async () => {
    if (!selectedMethod || !transferAmount) {
      toast.error(i18n.__('transferOut.toast.noMethodOrAmount'));
      return;
    }

    if (selectedMethod.methodType === 'paypal') {
      const paypalEmail = selectedMethod.details.email;
      if (!paypalEmail || !isPayPalPayoutDestinationReady(transferOutPreferences)) {
        toast.error(i18n.__('transferOut.toast.verifyPaypalProfile'));
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(paypalEmail)) {
        toast.error(i18n.__('transferOut.toast.invalidEmail'));
        return;
      }
    }

    if (selectedMethod.methodType === 'stripe-connect') {
      const sid = (transferOutPreferences?.stripeConnectId ?? '').trim();
      if (!sid || !isStripeConnectTransferOutAvailable(transferOutPreferences)) {
        toast.error(i18n.__('transferOut.toast.errorTransfer'));
        return;
      }
    }

    const amount = parseFloat(transferAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error(i18n.__('transferOut.toast.invalidAmount'));
      return;
    }

    if (amount > balance) {
      toast.error(i18n.__('transferOut.toast.insufficientBalance'));
      return;
    }

    setTransferLoading(true);

    try {
      if (selectedMethod.methodType === 'paypal' && selectedMethod.id === 'paypal-payout') {
        const result = await platformApi.paypalPayout({
          amount,
          currency: cur,
          paypalEmail: selectedMethod.details.email
        });

        toast.success(
          `Transfer successful! ${amount} ${cur} transferred to ${result.recipient}. Payout ID: ${result.payoutId}`
        );
      } else if (selectedMethod.methodType === 'stripe-connect' && selectedMethod.id === 'stripe-connect-payout') {
        await platformApi.stripeConnectPayout({
          amount,
          currency: cur,
          stripeConnectId: String(transferOutPreferences.stripeConnectId).trim(),
        });
        toast.success(`Transfer initiated! ${amount} ${cur} transfer has been processed.`);
      } else {
        await platformApi.transferOut({
          amount,
          currency: cur,
          methodId: selectedMethod.id,
          method: selectedMethod.methodType === 'plaid' ? 'plaid' : selectedMethod.methodType
        });

        toast.success(`Transfer initiated! ${amount} ${cur} transfer has been processed.`);
      }

      setTimeout(() => {
        navigate('/profile');
      }, 2000);

    } catch (error: any) {
      console.error('Transfer failed:', error);
      toast.error(error.message || i18n.__('transferOut.toast.errorTransfer'));
    } finally {
      setTransferLoading(false);
    }
  };

  const getMethodIcon = (methodType: TransferMethodType) => {
    switch (methodType) {
      case 'paypal':
        return <Wallet className="w-5 h-5" />;
      case 'stripe':
      case 'stripe-connect':
        return <CreditCard className="w-5 h-5" />;
      case 'plaid':
        return <Building2 className="w-5 h-5" />;
      default:
        return <Wallet className="w-5 h-5" />;
    }
  };

  const getMethodDisplayName = (method: TransferMethodRow) => {
    if (method.methodType === 'stripe-connect') {
      const raw = (transferOutPreferences?.stripeConnectId ?? '').trim();
      const short = raw.length > 14 ? `${raw.slice(0, 10)}…` : raw;
      return short ? `${i18n.__('transferOut.stripeConnect')} (${short})` : i18n.__('transferOut.stripeConnect');
    }
    switch (method.type) {
      case 'paypal':
        return method.details.email ?? '';
      case 'stripe':
        return `${method.details.brand?.toUpperCase()} •••• ${method.details.last4}`;
      case 'plaid':
        return `${method.details.accountName} (${method.details.accountType})`;
      default:
        return 'Unknown method';
    }
  };

  const availableMethods = getAvailableTransferMethods();
  const canTransfer = balance > 0;
  const hasPaymentMethods = availableMethods.length > 0;
  const transferAmountNum = parseFloat(transferAmount) || 0;
  const totalWithFees = transferAmountNum + estimatedFees.amount;
  const paypalNeedsSetup =
    isPaypalTransferOutChannelEnabled(transferOutPreferences) &&
    !!acceptableCurrencies.paypal?.includes(cur) &&
    !isPayPalPayoutDestinationReady(transferOutPreferences);

  const stripeConnectNeedsSetup =
    isMerchant &&
    !!acceptableCurrencies.stripe?.includes(cur) &&
    transferOutPreferences?.transferOutPreferences?.stripe?.enabled !== false &&
    !isStripeConnectTransferOutAvailable(transferOutPreferences);

  const bankMethodsForCurrency =
    isBankTransferOutChannelEnabled(transferOutPreferences) && acceptableCurrencies.bank.includes(cur)
      ? mergedBankMethods.filter((m) => m.type === 'plaid')
      : [];

  const bankNeedsSetup =
    isBankTransferOutChannelEnabled(transferOutPreferences) &&
    acceptableCurrencies.bank.includes(cur) &&
    bankMethodsForCurrency.length === 0;

  const showPayoutSetupPanel =
    paypalNeedsSetup || stripeConnectNeedsSetup || bankNeedsSetup;

  /** No usable payout methods yet → show profile payout setup before method + transfer block. */
  const payoutSetupBeforeTransferBlock = showPayoutSetupPanel && !hasPaymentMethods;
  /** At least one method exists but other channels still need setup → payout card below. */
  const payoutSetupAfterTransferBlock = showPayoutSetupPanel && hasPaymentMethods;

  const usesTotalDeducted =
    selectedMethod &&
    (selectedMethod.methodType === 'plaid' || selectedMethod.methodType === 'stripe-connect');

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p>{i18n.__('transferOut.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        
        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/profile')}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{i18n.__('transferOut.backToProfile')}</span>
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center space-x-2">
                <ArrowUpRight className="w-6 h-6" />
                <span>{i18n.__('transferOut.title')}</span>
              </h1>
              <p className="text-sm text-muted-foreground">
                {i18n.__('transferOut.subtitle', { currency: cur })}
              </p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <CardTitle className="flex items-center space-x-2">
                <Wallet className="w-5 h-5" />
                <span>{i18n.__('transferOut.currentBalance')}</span>
              </CardTitle>
              {availableCurrencyOptions.length > 0 && (
                <div className="flex items-center gap-2">
                  <Label htmlFor="transfer-out-currency" className="text-xs text-muted-foreground">
                    Currency
                  </Label>
                  <Select value={cur} onValueChange={handleCurrencyChange}>
                    <SelectTrigger id="transfer-out-currency" className="w-[160px]">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCurrencyOptions.map(([code, amount]) => (
                        <SelectItem key={code} value={code}>
                          {code}{amount > 0 ? ` • ${amount.toFixed(2)}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center p-4">
              <div className="text-3xl font-bold">
                <CurrencyDisplay amount={balance} currency={cur} currencyService={currencyService} size="lg" />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {i18n.__('transferOut.available')}
              </p>
            </div>
          </CardContent>
        </Card>

        {!canTransfer && !hasPaymentMethods ? (
          <Card>
            <CardContent className="p-6">
              <div className="text-center space-y-4">
                <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto" />
                <div>
                  <h3 className="text-lg font-semibold">{i18n.__('transferOut.cannotTransfer')}</h3>
                  <p className="text-muted-foreground">
                    {!acceptableCurrencies.paypal?.includes(cur) 
                      ? i18n.__('transferOut.unsupportedCurrency', { currency: cur })
                      : i18n.__('transferOut.noMethods', { currency: cur })
                    }
                  </p>
                </div>
                {acceptableCurrencies.paypal?.includes(cur) && (
                  <Button onClick={() => navigate('/payment-sources')} variant="outline">
                    {i18n.__('transferOut.addMethod')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {!canTransfer && (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  {i18n.__('transferOut.noBalance', { currency: cur })}
                </AlertDescription>
              </Alert>
            )}

            <TransferOutAmountCard
              balance={balance}
              transferAmount={transferAmount}
              onTransferAmountChange={setTransferAmount}
              amountInputId="amount"
              labels={{
                amountTitle: i18n.__('transferOut.amountTitle'),
                amountSubtitle: i18n.__('transferOut.amountSubtitle', {
                  balance: String(balance),
                  currency: cur,
                }),
                amountLabel: i18n.__('transferOut.amountLabel', { currency: cur }),
                amountPlaceholder: i18n.__('transferOut.amountPlaceholder'),
                max: i18n.__('transferOut.max'),
                pct25: i18n.__('transferOut.pct25'),
                pct50: i18n.__('transferOut.pct50'),
                pct75: i18n.__('transferOut.pct75'),
              }}
            />

            {payoutSetupBeforeTransferBlock && payoutsProfileSnapshot && (
              <ProfileTransferOutPayoutsCard
                profile={payoutsProfileSnapshot}
                editing={true}
                onEditingChange={() => {}}
                hideEditToggle
                onRefresh={loadTransferData}
                className="scroll-mt-2"
              />
            )}

            <Card>
              <CardHeader>
                <CardTitle>{i18n.__('transferOut.selectMethod')}</CardTitle>
                <CardDescription>
                  {i18n.__('transferOut.selectMethodSubtitle', { currency: cur })}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {availableMethods.map((method) => (
                  <TransferOutMethodOption
                    key={method.id}
                    selected={selectedMethod?.id === method.id}
                    onSelect={() => setSelectedMethod(method)}
                    icon={getMethodIcon(method.methodType)}
                    primaryText={getMethodDisplayName(method)}
                    secondaryText={
                      (method.methodType === 'paypal'
                        ? i18n.__('transferOut.paypal')
                        : method.methodType === 'stripe-connect'
                          ? i18n.__('transferOut.stripeConnect')
                          : method.methodType === 'stripe'
                            ? i18n.__('transferOut.creditCard')
                            : i18n.__('transferOut.bankAccount')) +
                      (method.isDefault ? ` • ${i18n.__('transferOut.default')}` : '')
                    }
                    showSelectedCheck={
                      method.methodType !== 'paypal' || !!method.details?.email
                    }
                  />
                ))}

                <Separator className="my-4" />

                <div className="space-y-3">
                  <p className="text-base font-semibold">{i18n.__('transferOut.summaryTitle')}</p>
                  {transferAmountNum > 0 && selectedMethod ? (
                    <>
                      <div className="flex justify-between">
                        <span>{i18n.__('transferOut.amountFromBalance')}</span>
                        <span><CurrencyDisplay amount={transferAmountNum} currency={cur} currencyService={currencyService} /></span>
                      </div>
                      {selectedMethod.methodType === 'paypal' && (
                        <>
                          <div className="flex justify-between text-sm text-muted-foreground">
                            <span>{i18n.__('transferOut.paypalFee')}</span>
                            <span>- <CurrencyDisplay amount={estimatedFees.amount} currency={cur} currencyService={currencyService} /></span>
                          </div>
                          <Separator />
                          <div className="flex justify-between font-semibold text-green-600">
                            <span>{i18n.__('transferOut.youWillReceive')}</span>
                            <span><CurrencyDisplay amount={Math.max(0, transferAmountNum - estimatedFees.amount)} currency={cur} currencyService={currencyService} /></span>
                          </div>
                          <Alert className="mt-2 py-2">
                            <AlertDescription className="text-xs">
                              <strong>{i18n.__('common.note')}:</strong> {i18n.__('transferOut.feeNote', { fee: `${(estimatedFees.percentage * 100).toFixed(1)}% + ${estimatedFees.fixed}` })}
                            </AlertDescription>
                          </Alert>
                        </>
                      )}
                      {selectedMethod.methodType === 'plaid' && (
                        <>
                          <div className="flex justify-between text-sm text-muted-foreground">
                            <span>{i18n.__('transferOut.bankFee')}</span>
                            <span><CurrencyDisplay amount={estimatedFees.amount} currency={cur} currencyService={currencyService} /></span>
                          </div>
                          <Separator />
                          <div className="flex justify-between font-semibold">
                            <span>{i18n.__('transferOut.totalDeducted')}</span>
                            <span><CurrencyDisplay amount={totalWithFees} currency={cur} currencyService={currencyService} /></span>
                          </div>
                        </>
                      )}
                      {selectedMethod.methodType === 'stripe-connect' && (
                        <>
                          <div className="flex justify-between text-sm text-muted-foreground">
                            <span>{i18n.__('transferOut.stripeFee')}</span>
                            <span><CurrencyDisplay amount={estimatedFees.amount} currency={cur} currencyService={currencyService} /></span>
                          </div>
                          <Separator />
                          <div className="flex justify-between font-semibold">
                            <span>{i18n.__('transferOut.totalDeducted')}</span>
                            <span><CurrencyDisplay amount={totalWithFees} currency={cur} currencyService={currencyService} /></span>
                          </div>
                          <Alert className="mt-2 py-2">
                            <AlertDescription className="text-xs">
                              <strong>{i18n.__('common.note')}:</strong>{' '}
                              {i18n.__('transferOut.stripeFeeNote', { fee: `${(estimatedFees.percentage * 100).toFixed(1)}% + ${estimatedFees.fixed}` })}
                            </AlertDescription>
                          </Alert>
                        </>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {i18n.__('transferOut.reviewTransferHint')}
                    </p>
                  )}
                </div>
              </CardContent>
              <CardFooter className="flex flex-col items-stretch gap-2 p-6 pt-0">
                <Button
                  onClick={handleTransferOut}
                  disabled={!selectedMethod || !transferAmount || transferLoading ||
                           (selectedMethod?.methodType === 'paypal' ? transferAmountNum > balance : totalWithFees > balance)}
                  className="w-full"
                  size="lg"
                >
                  {transferLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      {i18n.__('transferOut.processing')}
                    </>
                  ) : (
                    <>
                      <ArrowUpRight className="w-4 h-4 mr-2" />
                      {i18n.__('transferOut.transferFunds')}
                    </>
                  )}
                </Button>
                {((selectedMethod?.methodType === 'paypal' && transferAmountNum > balance) ||
                  (selectedMethod && usesTotalDeducted && totalWithFees > balance)) && (
                  <p className="text-sm text-destructive text-center">
                    {i18n.__('transferOut.insufficientBalance')}
                  </p>
                )}
              </CardFooter>
            </Card>

            {payoutSetupAfterTransferBlock && payoutsProfileSnapshot && (
              <ProfileTransferOutPayoutsCard
                profile={payoutsProfileSnapshot}
                editing={true}
                onEditingChange={() => {}}
                hideEditToggle
                onRefresh={loadTransferData}
                className="scroll-mt-2"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TransferOutPage;
